'use client'

import * as React from 'react'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { mtc } from '../../../utils/style-helpers'
import { Icon } from '../icon'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import {
  subTriggerStyles,
  subContentStyles,
  contentStyles,
  itemStyles,
  checkboxItemStyles,
  radioItemStyles,
  labelStyles,
  separatorStyles,
  shortcutStyles,
} from './styles'

const SubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={mtc(subTriggerStyles({ inset, className }))}
    {...props}
  >
    {children}
    <Icon icon={solid('chevron-right')} className="ml-auto" />
  </DropdownMenuPrimitive.SubTrigger>
))
SubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName

const SubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={mtc(subContentStyles({ className }))}
    {...props}
  />
))
SubContent.displayName = DropdownMenuPrimitive.SubContent.displayName

const Content = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={mtc(contentStyles({ className }))}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
))
Content.displayName = DropdownMenuPrimitive.Content.displayName

const Item = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={mtc(itemStyles({ inset, className }))}
    {...props}
  />
))
Item.displayName = DropdownMenuPrimitive.Item.displayName

const CheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={mtc(checkboxItemStyles({ className }))}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Icon icon={solid('check')} className="h-4 w-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
))
CheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName

const RadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={mtc(radioItemStyles({ className }))}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Icon icon={solid('circle')} className="h-2 w-2 fill-current" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
))
RadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

const Label = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={mtc(labelStyles({ inset, className }))}
    {...props}
  />
))
Label.displayName = DropdownMenuPrimitive.Label.displayName

const Separator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={mtc(separatorStyles({ className }))}
    {...props}
  />
))
Separator.displayName = DropdownMenuPrimitive.Separator.displayName

const Shortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return <span className={mtc(shortcutStyles({ className }))} {...props} />
}
Shortcut.displayName = 'DropdownMenuShortcut'

const Trigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger> & {
    disabled?: boolean
  }
>(({ className, disabled, ...props }, ref) => (
  <DropdownMenuPrimitive.Trigger
    ref={ref}
    className={mtc({ className, disabled })}
    disabled={disabled}
    {...props}
  />
))
Trigger.displayName = DropdownMenuPrimitive.Trigger.displayName

const Dropdown =
  DropdownMenuPrimitive.Root as typeof DropdownMenuPrimitive.Root & {
    Content: typeof DropdownMenuPrimitive.Content
    Item: typeof DropdownMenuPrimitive.Item
    CheckboxItem: typeof DropdownMenuPrimitive.CheckboxItem
    RadioItem: typeof DropdownMenuPrimitive.RadioItem
    Label: typeof DropdownMenuPrimitive.Label
    Separator: typeof DropdownMenuPrimitive.Separator
    Shortcut: typeof Shortcut
    SubContent: typeof DropdownMenuPrimitive.SubContent
    SubTrigger: typeof DropdownMenuPrimitive.SubTrigger
    Trigger: typeof DropdownMenuPrimitive.Trigger
    Group: typeof DropdownMenuPrimitive.DropdownMenuGroup
    Portal: typeof DropdownMenuPrimitive.DropdownMenuPortal
    Sub: typeof DropdownMenuPrimitive.DropdownMenuSub
    RadioGroup: typeof DropdownMenuPrimitive.DropdownMenuRadioGroup
    Root: typeof DropdownMenuPrimitive.Root
  }
Dropdown.Content = Content
Dropdown.Item = Item
Dropdown.CheckboxItem = CheckboxItem
Dropdown.RadioItem = RadioItem
Dropdown.Label = Label
Dropdown.Separator = Separator
Dropdown.Shortcut = Shortcut
Dropdown.SubContent = SubContent
Dropdown.SubTrigger = SubTrigger
Dropdown.Trigger = DropdownMenuPrimitive.Trigger
Dropdown.Group = DropdownMenuPrimitive.Group
Dropdown.Portal = DropdownMenuPrimitive.Portal
Dropdown.Sub = DropdownMenuPrimitive.Sub
Dropdown.RadioGroup = DropdownMenuPrimitive.RadioGroup
Dropdown.Root = DropdownMenuPrimitive.Root
export { Dropdown }
